import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModuleDefinition } from '@platform/modularity/useModuleDefinition';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { ErrorBanner, ModuleTile, Spinner } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { PageFrame } from '@platform/engine/list/header/PageFrame';
import { PageHeader } from '@platform/engine/list/header/PageHeader';
import { useModuleCrumbs } from '@platform/engine/list/header/useModuleCrumbs';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { moduleNav } from '@platform/navigation';
import { DataModelBody } from './design/DataModelBody';
import { DesignListSection } from './design/DesignListSection';
import { designSectionKeys, isDesignSection } from './design/designSections';
import './design/design.css';

/**
 * /:group/:module/design/:item — група «Проєктування» модуля за прототипом v4: функції, мотивація, зацікавлені сторони й потреби,
 * шляхи користувачів, сценарії використання, вимоги — списки записів бізнес-аналізу модуля (DesignListSection); модель даних — типи
 * записів, атрибути, статуси й діаграма (DataModelBody). Шапка: крихти «… › Модуль › Проєктування · Розділ», плитка модуля,
 * «МОДУЛЬ · ПРОЄКТУВАННЯ» над назвою розділу (22px); у моделі даних — ще й опис (проміжки рядків шапки — як у прототипі: 3px у моделі,
 * 0 у списках, design.css).
 */
export function ModuleDesignPage() {
  const { ws, module: moduleKey, item } = useWorkspaceParams();
  const { t } = useTranslation();
  const { data: module, isLoading, error } = useModuleDefinition(moduleKey);
  const strings = useModuleStrings(moduleKey);
  const moduleName = module ? strings.moduleName(module.module.name) : '';
  const known = isDesignSection(item);
  const section = known ? t(`design.${item}`) : '';
  const tail = useMemo(() => [{ label: `${t('pageHead.design')} · ${section}` }], [t, section]);
  const crumbs = useModuleCrumbs(moduleKey, moduleName, tail);
  if (isLoading) return <Spinner />;
  if (error || !module) return <ErrorBanner error={error ?? { message: t('app.notFound') }} />;
  if (!isDesignSection(item)) return <Navigate to={routes.moduleDesign(moduleKey, designSectionKeys[0])} replace />;
  const nav = moduleNav(moduleKey);
  const eyebrow = `${moduleName} · ${t('pageHead.design')}`;
  if (item === 'model') {
    const head = (
      <div className="md-head md-head--model">
        <PageHeader tile={<ModuleTile icon={nav.icon} tone={nav.tone} size={44} />} eyebrow={eyebrow} title={section} description={t('modDesign.model.subtitle', { name: moduleName })} />
      </div>
    );
    return <PageFrame crumbs={crumbs} head={head}><DataModelBody ws={ws} module={module} /></PageFrame>;
  }
  const header = <div className="md-head md-head--list"><PageHeader tile={<ModuleTile icon={nav.icon} tone={nav.tone} size={36} />} eyebrow={eyebrow} title={section} /></div>;
  return <DesignListSection key={item} ws={ws} host={module} section={item} crumbs={crumbs} header={header} />;
}
